<template>
  <div class="book" id="bookingForm">
    <div class="min-h-screen relative z-10 px-4 lg:px-0">
        <Form class="flex flex-wrap lg:flex-nowrap gap-8 sm:px-8"
              v-slot="{ errors, invalid, failed }"
              @submit="onSubmit"
              @invalid-submit="onInvalidSubmit">
          <div class="w-full lg:max-w-[832px] flex-grow md:pr-4 lg:pb-20 relative">
            <Loader v-if="bookingProcessing">
              <h3 class="text-center text-xl">Processing booking...</h3>
            </Loader>
            <div class="mt-6 lg:mt-10 mb-10 lg:mb-20 h-[60px]">
              <h2
                  class="text-4xlx flex items-center gap-2 font-semibold text-dark"
              >
                <svg width="50" height="45" viewBox="0 0 50 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.91133 18.3656L32.4644 1.3031C34.7733 -0.029908 37.7908 0.778636 39.1238 3.08747L41.4471 7.11145C41.8279 7.77112 41.7089 8.63142 41.2295 9.17209C40.2427 10.3575 40.0707 12.04 40.8324 13.3593C41.6322 14.7446 43.1753 15.4369 44.6953 15.1751C45.4033 15.0302 46.2078 15.3573 46.5887 16.017L48.8738 19.975C50.2449 22.3498 49.3983 25.3013 47.0894 26.6343L17.5363 43.6968C15.1615 45.0679 12.248 44.2873 10.877 41.9125L8.5918 37.9545C8.21094 37.2948 8.26397 36.4726 8.80938 35.8938C9.73018 34.7465 9.96816 33.0259 9.16836 31.6406C8.40664 30.3213 6.79756 29.6671 5.34355 29.8908C4.56963 30.0738 3.83105 29.7086 3.4502 29.0489L1.12695 25.025C-0.206055 22.7161 0.536523 19.7367 2.91133 18.3656ZM4.29336 23.1968L6.00723 26.1654C8.50176 26.2204 11.0018 27.5036 12.3348 29.8125C13.7059 32.1873 13.5672 34.994 12.3676 37.1818L14.0434 40.0843C14.3861 40.678 15.1145 40.8732 15.7082 40.5304L45.2613 23.4679C45.7891 23.1633 46.0502 22.3968 45.7074 21.8031L44.0316 18.9006C41.4711 18.8836 39.0371 17.5623 37.666 15.1875C36.333 12.8786 36.4057 10.11 37.6713 7.8841L35.9574 4.9156C35.6527 4.38786 34.8203 4.16482 34.2926 4.46951L4.73945 21.532C4.14575 21.8748 3.98867 22.6691 4.29336 23.1968ZM34.2168 21.4008L30.5605 15.0679L15.784 23.5992L19.4402 29.932L34.2168 21.4008ZM12.0082 24.3718C11.3607 23.2504 11.713 21.7277 12.9004 21.0422L29.7879 11.2922C30.9093 10.6447 32.4701 11.0629 33.1176 12.1843L37.9926 20.6281C38.6781 21.8155 38.2218 23.3103 37.1004 23.9578L20.2129 33.7078C19.0255 34.3933 17.5687 34.003 16.8832 32.8156L12.0082 24.3718Z" fill="currentColor"/>
                </svg>

                <span>Book Tickets</span>
              </h2>
            </div>
            <div v-if="hasAttractions" class="space-y-10">
              <div v-for="(item, ind) in items" :key="item.id">

                <transition name="fade">
                  <div v-if="item && loaded">
                    <Field
                        v-slot="{ field, errors }"
                        label="Experience"
                        :name="`item_${item.id}_attraction`"
                        rules="required|not_empty"
                        v-model="item.attraction"
                        tag="div"
                    >
                        <ErrorWrapper :submitted="submitted" :errors="errors">
                          <attraction-select
                              v-bind="field"
                              @setAttraction="setAttraction(ind, $event)"
                              v-model="item.attraction"
                              v-model:product-code="item.product_code"
                              v-model:time="item.time"
                              :spacial="special"
                              :removable="items.length > 1"
                              :errors="submitted ? errors : []"
                              @remove="removeItem(item)"
                          ></attraction-select>
                        </ErrorWrapper>
                    </Field>

                    <div v-if="item.attraction">
                      <div v-show="findAttraction(item.attraction)">
                        <Field
                            v-slot="{ field, errors }"
                            label="Option"
                            :name="`item_${item.id}_product_code`"
                            :rules="findAttraction(item.attraction).rezdy_product_options && findAttraction(item.attraction).rezdy_product_options.length > 0 ? 'required' : ''"
                            v-model="item.product_code"
                            v-model:time="item.time"
                            tag="div"
                        >
                            <attraction-info-options
                                :submitted="submitted"
                                :attraction="findAttraction(item.attraction)"
                                v-bind="field"
                                :errors="submitted ? errors : []"
                            />
                        </Field>
                      </div>
                    </div>

                      <Field
                          v-if="!walkInOnly(item.attraction)"
                          v-slot="{ field, errors }"
                          :label="`date and time`"
                          :name="`item_${item.id}_date`"
                          rules="date_required"
                          v-model="item.time"
                          tag="div"
                      >
                          <session
                              v-bind="field"
                              :attraction="findAttraction(item.attraction)"
                              :dates="dates"
                              :errors="submitted ? errors : []"
                              :test="test"
                              v-model="item.time"
                              v-model:prices="item.prices"
                              v-model:availability="item.availability"
                              :number="(ind * 2) + 1"
                              :submitted="submitted"
                              :product-code="item.product_code"
                          ></session>
                      </Field>


                    <Field
                        v-if="!walkInOnly(item.attraction)"
                        v-slot="{ field, errors }"
                        v-model="item.participants"
                        label="Participants"
                        :name="`item_${item.id}_participants`"
                        :rules="{
                          required: true,
                          participants: true,
                          max_participants: maxParticipants,
                          participants_require_adult_or_senior: findAttraction(item.attraction) && !findAttraction(item.attraction).allow_children_only
                        }"
                        immediate
                        class="participants-fields"
                        tag="div"
                    >
                        <dynamic-participants
                            :prices="item.prices"
                            :availability="item.availability"
                            :number="(ind * 2) + 2"
                            :errors="errors ? errors : []"
                            v-bind="field"
                            :submitted="submitted"
                            :attraction="this.findAttraction(item.attraction)"
                            v-model="item.participants"
                            :time="item.time"
                        ></dynamic-participants>
                    </Field>
                  </div>
                </transition>

              <AddAnother @add-another="addAnother" v-if="ind === (items.length - 1)"/>

              <ExtrasOptions
                  v-if="findAttraction(item.attraction)?.extras?.length > 0"
                  :extras="findAttraction(item.attraction).extras"
                  v-model="item.extras" />

              <div v-if="ind === 0 && firstAttraction.extra_attraction_id > 0 && (items.length < 2 || special)" class="mt-10 lg:mt-20 p-10 pb-14 relative rounded-lg bg-light overflow-hidden">
                <div class="mb-8" v-if="false">
                  <span class="inline-flex text-mdx font-semibold p-2 px-4 leading-none bg-primary rounded-full uppercase">
                    Special offer
                  </span>
                </div>
                <h2 v-text="firstAttraction.extra_attraction_title"
                    class="font-bold text-xl lg:text-3xl text-dark mb-4"></h2>

                <div
                    class="mb-4 text-dark text-xl"
                    v-text="firstAttraction.extra_attraction_text"
                ></div>

                <alert-error v-if="!special && items.length > 1">You can only use one promotion at a time.</alert-error>

                <button
                    v-if="!special && items.length < 2"
                    :disabled="items.length > 1"
                    class="flex justify-center items-center gap-2 font-semibold text-xlx"
                    @click.prevent="selectExtraAttraction(firstAttraction.extra_attraction_id, true)"
                >
                  <span  v-text="firstAttraction.extra_attraction_button"></span>
                  <span class="bg-primary text-dark h-12 w-12 rounded-full flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1.5V6H12.5C13.0312 6 13.5 6.46875 13.5 7C13.5 7.5625 13.0312 8 12.5 8H8V12.5C8 13.0625 7.53125 13.5 7 13.5C6.4375 13.5 6 13.0625 6 12.5V8H1.5C0.9375 8 0.5 7.5625 0.5 7C0.5 6.46875 0.9375 6 1.5 6H6V1.5C6 0.96875 6.4375 0.5 7 0.5C7.53125 0.5 8 0.96875 8 1.5Z"
                              fill="currentColor" />
                    </svg>
                  </span>
                </button>

                <button
                    v-if="special"
                    class="button bg-red-600 rounded-full text-white"
                    @click.prevent="maybeRemoveSecondAttraction()"
                >
                  Remove
                </button>

                <img class="absolute scale-110 -bottom-[11px] left-0 w-full" :src="`${$settings.theme_url}/assets/images/wave-bottom-block.svg`" alt="Ochre wave">
              </div>

              </div>
              <!-- end items -->
            </div>

            <alert-error v-if="toClose">
              The times you have selected are too close together.
              Please adjust your selection to allow more time for travel and between activities.
            </alert-error>



            <div>
              <SectionTitle :number="(items.length * 2) + 1" class="mt-12 lg:mt-20">
                Checkout
              </SectionTitle>

              <PaymentStripeExpress
                  v-if="options.gateway.express"
                  :config="options.gateway"
                  :pre-checkout-validator="() => validateBeforeExpressCheckout()"
                  ref="paymentExpress"
                  @errors="addPaymentErrors"
                  @setPage="setPage"
                  @top="top"
                  @paymentConfirmed="expressPaymentConfirmed"
              ></PaymentStripeExpress>

              <div class="mb-4">
                <div class="grid md:grid-cols-2 gap-4">
                  <Field
                      v-slot="{ field, errors }"
                      v-model="customer.firstName"
                      label="First name"
                      name="firstName"
                      rules="required" slim>
                    <div class="customer-field mb-4">
                      <ErrorWrapper :submitted="submitted" :errors="errors">
                        <FieldLabel for="firstName" :required="true">First Name</FieldLabel>
                        <div class="flex-grow">
                          <input
                              v-bind="field"
                              type="text"
                              data-testid="booking-first-name"
                              class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                              :class="{ 'border border-alert-error': errors.length }"
                          />
                          <FieldError :errors="errors" />
                        </div>
                      </ErrorWrapper>
                    </div>
                  </Field>
                  <Field
                      v-slot="{ field, errors }"
                      v-model="customer.lastName"
                      label="Last name"
                      name="lastName"
                      rules="required" slim>
                    <div class="customer-field mb-4">
                      <ErrorWrapper :submitted="submitted" :errors="errors">
                        <FieldLabel for="lastName" :required="true">Last Name</FieldLabel>
                        <div class="flex-grow">
                          <input
                              v-bind="field"
                              type="text"
                              data-testid="booking-last-name"
                              class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                              :class="{ 'border-alert-error': errors.length }"
                          />
                          <FieldError :errors="errors" />
                        </div>
                      </ErrorWrapper>
                    </div>
                  </Field>
                </div>
                <Field
                    v-slot="{ field, errors }"
                    v-model="customer.email"
                    label="Email"
                    name="email"
                    rules="required|email">
                  <div class="customer-field mb-4">
                    <ErrorWrapper :submitted="submitted" :errors="errors">
                      <FieldLabel for="email" :required="true">Email</FieldLabel>
                      <div class="flex-grow">
                        <input
                          v-bind="field"
                          type="email"
                          data-testid="booking-email"
                          class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                          :class="{ 'border-alert-error': errors.length }"
                        />
                        <FieldError :errors="errors" />
                      </div>
                    </ErrorWrapper>
                  </div>
                </Field>
                <Field
                    v-slot="{ field, errors }"
                    :value="customer.phone"
                    label="Phone"
                    name="phone"
                    rules="required|international_phone"
                >
                  <div class="customer-field mb-2 w-full lg:w-2/3">
                    <ErrorWrapper :submitted="submitted" :errors="errors">
                      <FieldLabel for="phone" :required="true">Phone</FieldLabel>
                      <div class="flex-grow">
                        <IntlTelInput
                            :value="field.value"
                            :model-value="field.value"
                            @update:modelValue="field.onChange"
                            :input-props="{
                              onBlur: field.onBlur,
                              type: 'text',
                              name: field.name,
                              'data-testid': 'booking-phone',
                              class: 'w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white ' + (errors.length ? 'border-alert-error' : ''),
                            }"
                            @changeNumber="(num) => { field.onChange(num); updatePhoneNumber(num); }"
                            :options="{
                              initialCountry: 'au',
                            }" />
                        <FieldError :errors="errors" />
                      </div>
                    </ErrorWrapper>
                  </div>
                </Field>
              </div>

              <voucher
                v-model="vouchers"
                @details="voucherDetails"
              ></voucher>

              <div v-if="rcaCoupons" class="pb-4 px-6 relative">
                <Field
                  v-slot="{ field, errors }"
                  label="RCA membership number"
                  v-model="rcaNumber"
                  rules="required"
                  name="rcaNumber"
                >
                  <div class="customer-field mb-2">
                    <FieldLabel for="rcaNumber" :required="true">RCA membership number</FieldLabel>
                    <div class="flex-grow">
                      <input
                        id="rcaNumber"
                        v-bind="field"
                        type="text"
                        class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                        :class="{ 'border-alert-error': errors.length }"
                      />
                      <FieldError :errors="errors" />
                    </div>
                  </div>
                </Field>
              </div>

              <div v-if="options.gateway && options.gateway.type && needsPayment" v-cloak id="paymentForm" data-testid="booking-payment-form">
                <PaymentCard
                    v-if="options.gateway.type === 'card'"
                    :config="options.gateway"
                    ref="payment" />
                <PaymentStripeLegacy
                    v-if="['stripe:rezdy', 'stripe:via'].includes(options.gateway.type)"
                    :config="options.gateway"
                    ref="payment"
                    @errors="addPaymentErrors" />
                <PaymentStripe3d
                    v-if="options.gateway.type === 'stripe:3d'"
                    :config="options.gateway"
                    ref="payment"
                    @errors="addPaymentErrors"/>
                <PaymentStripe
                    v-if="options.gateway.type === 'stripe'"
                    :config="options.gateway"
                    ref="payment"
                    @errors="addPaymentErrors"
                    @setPage="setPage"
                    @top="top" />
              </div>
            </div>
          </div>
          <div class="w-full lg:max-w-[386px] relative pb-6">
            <div class="sticky top-[100px]">
              <div class="flex justify-end items-end h-[60px]"></div>

              <h3 class="mb-8 pt-2 text-xlx font-semibold">Booking Summary</h3>

              <quote
                ref="quote"
                data-testid="booking-quote"
                :vouchers="voucherInfo"
                :items="items"
                @loading="setQuoteLoading"
                @errors="addQuoteErrors"
              ></quote>

              <form-errors
                  :errors="errorList"
                  :submitted="submitted"
              ></form-errors>

              <questions
                  :submitted="submitted"
                  v-model="answered"
                  @errors="addQuestionErrors"
              ></questions>

              <confirmations
                  v-if="items.length > 0"
                  :submitted="submitted"
                  v-model="confirmed"
              ></confirmations>

              <alert-error v-if="toClose">
                The times you have selected are too close together.
                Please adjust your selection to allow more time for travel and between activities.
              </alert-error>

              <div class="py-4">
                <button
                  type="submit"
                  :disabled="toClose || loading || submitQuoteErrors.length > 0 || quoteLoading || !readyToPay"
                  data-testid="booking-submit"
                  class="bg-primary text-dark font-semibold text-center p-2 py-4 rounded-full w-full leading-none flex items-center justify-center gap-2 disabled:opacity-70"
                  @click="submitted = true"
                >
                  <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.7188 2.5 9 2.5C7.24219 2.5 5.875 3.90625 5.875 5.625ZM3.375 7.5V5.625C3.375 2.53906 5.875 0 9 0C12.0859 0 14.625 2.53906 14.625 5.625V7.5H15.25C16.6172 7.5 17.75 8.63281 17.75 10V17.5C17.75 18.9062 16.6172 20 15.25 20H2.75C1.34375 20 0.25 18.9062 0.25 17.5V10C0.25 8.63281 1.34375 7.5 2.75 7.5H3.375Z" fill="currentColor"/>
                  </svg>
                  <span v-if="loading && submitted">Processing...</span>
                  <span v-if="!loading">Complete Your Booking</span>
                </button>

                <terms class="mb-2"></terms>
                <div class="mt-2 text-sm">
                  <p>
                    This site is protected by reCAPTCHA and the Google
                    <a href="https://policies.google.com/privacy"
                       target="google_terms"
                       class="text-dark underline"
                      >Privacy Policy</a
                    >
                    and
                    <a href="https://policies.google.com/terms"
                       target="google_terms"
                       class="text-dark underline"
                      >Terms of Service</a
                    >
                    apply.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Form>
    </div>
  </div>
</template>
<script>
import {mapActions, mapWritableState} from "pinia";
import {Field, Form} from 'vee-validate';
import moment from "moment";
import {v4} from 'uuid';
import api from "./../api";
import useStore from "./../store"
import {trackBookingComplete} from "../helpers"

//components
import Session from "./../components/Session";
import AttractionSelect from "./../components/AttractionSelect";
import AttractionInfoOptions from "../components/AttractionInfoOptions.vue";
import DynamicParticipants from "./../components/DynamicParticipants";
import Quote from "./../components/Quote";
import Voucher from "./../components/Voucher";
import Terms from "./../components/Terms";
import FieldLabel from "./../components/FieldLabel";
import SectionTitle from "../components/SectionTitle.vue";
import FieldError from "../components/FieldError.vue";
import Confirmations from "../components/Confirmations.vue";
import AlertError from "../components/AlertError.vue";
import PaymentCard from "./../components/PaymentCard.vue";
import PaymentStripeLegacy from "../components/PaymentStripeLegacy.vue";
import PaymentStripe from "../components/PaymentStripe.vue";
import PaymentStripe3d from "../components/PaymentStripe3d.vue";
import quote from "../components/Quote.vue";
import FormErrors from "../components/FormErrors.vue";
import ErrorWrapper from "../components/ErrorWrapper.vue";
import AddAnother from "../components/AddAnother.vue";
import ExtrasOptions from "../components/ExtrasOptions.vue";
import Loader from "../components/Loader.vue";

//mixin
import DatesMixin from "./../mixins/dates";
import AttractionsMixin from "./../mixins/attraction";
import PaymentStripeExpress from "../components/PaymentStripeExpress.vue";
import useReadyToPay from "../helpers/useReadyToPay";
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";
import Questions from "../components/Questions.vue";


export default {
  components: {
    Questions,
    IntlTelInput,
    PaymentStripeExpress,
    ExtrasOptions,
    AddAnother,
    ErrorWrapper,
    PaymentStripe3d,
    PaymentStripe,
    PaymentCard,
    PaymentStripeLegacy,
    AlertError,
    Confirmations,
    FieldError,
    SectionTitle,
    DynamicParticipants,
    // Participants,
    AttractionSelect,
    AttractionInfoOptions,
    Session,
    Quote,
    Voucher,
    Terms,
    Form,
    Field,
    FieldLabel,
    FormErrors,
    Loader
  },
  setup() {
    const { readyToPay } = useReadyToPay();

    return {
      readyToPay
    }
  },
  mixins: [DatesMixin, AttractionsMixin],
  props: {
    code: {
      type: [String, Number, Boolean],
      default: false,
    },
    hideDateAndSessionSelection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hideAttractions: false,
      voucherInfo: false,
      submitErrors: [],
      submitQuoteErrors: [],
      paymentErrors: [],
      submitted: false,
      loading: false,
      quoteLoading: false,
      test: false,
      special: false,
      confirm: false,
      loaded: false,
      confirmed: false,
      answered: true,
      bookingProcessing: false,
    };
  },
  computed: {
    errorList(){
      return [...this.submitErrors, ...this.paymentErrors, ...this.submitQuoteErrors];
    },
    checkboxLabel() {
      return this.options.checkboxLabel;
    },
    checkboxDesc() {
      return this.options.checkboxDesc;
    },
    rcaCoupons() {
      if (this.vouchers.length < 1) {
        return false;
      }

      if (this.options.racCoupons < 1) {
        return false;
      }

      return (
        this.vouchers.filter((v) => {
          return this.options.racCoupons.filter((rc) => v.includes(rc)).length > 0;
        }).length > 0
      );
    },
    firstAttraction() {
      if (this.items.length < 1){
        return false;
      }

      return this.attractions.find(a => a.id === this.items[0].attraction) ?? false;
    },
    enableSecondAttraction() {
      return !!this.options.enableSecondAttraction;
    },
    sessionTimes() {
      return this.attractions.filter((a) => !!a.time);
    },
    maxParticipants() {
      let maxes = this.sessionTimes.map((a) => a.time.seatsAvailable);

      return maxes.length > 0 ? Math.min(...maxes) : false;
    },
    hasAttractions() {
      return this.items.length > 0;
    },
    ...mapWritableState(useStore, [
      "options",
      "items",
      "attractions",
      "customer",
      "creditCard",
      "voucher",
      "rcaNumber",
      "vouchers",
      "quote",
      "g-recaptcha-response",
      "status",
      "form",
      "totalPeople"
    ]),
    toClose() {

      if (this.items.length < 1) {
        return false;
      }

      for (let item of this.items){
        let compareAttraction = this.attractions.find(a => item.attraction === a.id)

        // no distance set, let's try the next one
        if (!compareAttraction?.distance || compareAttraction.distance.length < 1){
          continue;
        }

        for (let compareItem of this.items.filter((i) => i.id !== item.id)){

          let compareAttractionDistance = compareAttraction.distance.find(d => d.attraction === compareItem.attraction);

          // No hours set so ignore and try the next
          if (compareAttractionDistance?.hours && parseFloat(compareAttractionDistance.hours) === 0) {
            continue;
          }

          if (
              !compareItem.hasOwnProperty("time") ||
              !item.hasOwnProperty("time")
          ) {
            continue;
          }

          let from = moment(item.time).subtract(
              parseFloat(compareAttractionDistance?.hours ?? 1),
              "hours"
          );
          let to = moment(item.time).add(
              parseFloat(compareAttractionDistance?.hours ?? 1),
              "hours"
          );

          if(moment(compareItem.time).isBetween(from, to)){
            return true;
          } else {
            continue;
          }

        }
      }

      return false;
    },
    needsPayment() {
      return !(this.quote.totalDue === 0 && this.vouchers.length > 0);
    },
  },
  beforeMount() {
    (async () => {
      try {
        await this.loadAttractions();
      } catch (error) {
        // BookWidget reports the failure and offers a retry, which remounts this form
        return;
      }

      if (window?.fathom) {
        window.fathom.trackEvent('booking-form-opened');
      }

      if (this.code) {
        const matched = this.attractions.find((att) => att.code === this.code);
        if (matched) {
          this.setItems([{
            attraction: matched.id,
            id: v4(),
            product_code: 0,
            prices: [],
            extras: [],
            availability: {},
          }]);

          if (window?.fathom) {
            window.fathom.trackEvent('booking-select-' + this.code);
          }
        } else {
          console.warn(`[BookWidget] Attraction code "${this.code}" not found; falling back to manual selection.`);
        }
      }

      this.loaded = true;
    })();
  },
  methods: {
    ...mapActions(useStore, {
      loadAttractions: "loadAttractions",
      setStatus: "setStatus",
      setBooking: "setBooking",
      setBookingToken: "setBookingToken",
      setItems: "setItems",
      addItem: "addItem",
      removeSingleItem: "removeItem",
    }),
    updatePhoneNumber(number){
      this.customer.phone = number;
    },
    setQuoteLoading(loading) {
      this.quoteLoading = loading;
    },
    setAttraction(ind, data) {
      console.log({
        ind,
        data,
      })
    },
    removeItem(attraction) {
      this.removeSingleItem(attraction);
      this.special = false;
    },
    addAnother(){
      this.addItem({
        attraction: 0,
        id: v4(),
        extras: []
      })
    },
    voucherDetails(data) {
      this.voucherInfo = data;
      this.paymentErrors = [];
    },
    selectExtraAttraction(id, special = false) {
      this.addItem({
        attraction: id,
        id: v4(),
        extras: []
      })

      this.special = special;
    },
    maybeRemoveSecondAttraction() {
      if (this.items.length > 1) {
        this.items.pop();
        this.special = false;
        return true;
      }

      return false;
    },
    async top() {
      this.$emit('top')
    },
    async setPage(page) {
      this.$emit('setPage', page)
    },
    async recaptcha() {
      // Try twice - network glitches on mobile are often momentary
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          await this.$recaptchaLoaded();
          this["g-recaptcha-response"] = await this.$recaptcha("login");
          return; // Success
        } catch (error) {
          console.warn(`reCAPTCHA attempt ${attempt}/2 failed:`, error);
          if (attempt < 2) {
            // Brief pause before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // Final attempt failed - continue without token
            // Server-side can handle missing reCAPTCHA
            this["g-recaptcha-response"] = "";
          }
        }
      }
    },
    async onSubmit() {
      this.submitted = true;
      const validForBooking = await this.validateBeforeBooking();
      if (!validForBooking) {
        return;
      }
      this.submitErrors = [];
      this.paymentErrors = [];
      this.loading = true;

      if (!this.needsPayment){
        await this.recaptcha();
      }

      if (this.needsPayment && ['stripe', 'stripe:3d'].includes(this.options.gateway.type)){
        const validToken = await this.$refs.payment.generate();
        if (!validToken){
          this.loading = false;
          return;
        }
      }

      await this.book();
    },
    async expressPaymentConfirmed() {
      this.submitted = true;
      const validForBooking = await this.validateBeforeBooking();
      if (!validForBooking) {
        return;
      }
      this.submitErrors = [];
      this.paymentErrors = [];
      this.loading = true;

      if (!this.needsPayment){
        await this.recaptcha();
      }

      await this.book('express');
    },
    async validateBeforeExpressCheckout() {
      this.submitted = true;
      this.submitErrors = [];
      this.paymentErrors = [];

      return await this.validateBeforeBooking();
    },
    async validateBeforeBooking() {
      const questionsError = 'Please answer the required questions';
      this.submitErrors = this.submitErrors.filter(error => error !== questionsError);
      if (!this.answered){
        this.submitErrors.push(questionsError);
        await this.$nextTick();
        const questionInput = document.querySelector('#bookingForm [id^=\"answer_\"]');

        if (questionInput) {
          questionInput.focus();
          questionInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return false;
      }

      if (!this.confirmed){
        await this.$nextTick();
        const confirmationInput = document.querySelector('#bookingForm [id^=\"confirmation_\"]');

        if (confirmationInput) {
          confirmationInput.focus();
          confirmationInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return false;
      }

      if (this.toClose){
        return false;
      }

      return true;
    },
    focusFirstError(errors) {
      const errorKeys = Object.keys(errors);

      const fieldsToMoveToEnd = ['firstName', 'lastName', 'email', 'phone'];
      const fieldsSet = new Set(fieldsToMoveToEnd);

      const reorderedErrors = [
        ...errorKeys.filter(field => !fieldsSet.has(field)),
        ...errorKeys.filter(field => fieldsSet.has(field))
      ];

      const firstErrorKey = reorderedErrors[0];
      const errorInput = document.querySelector(`#bookingForm [name="${firstErrorKey}"]`);

      if (errorInput) {
        errorInput.focus();
        errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    async book(mode = 'payment') {
      this.bookingProcessing = true;

      this.setStatus(null);
      let savedForm = this.form;
      savedForm.mode = mode

      return api
          .order(savedForm)
          .then(async (response) => {
            if (response.data.handle && this.options.gateway.type === 'stripe') {
              if (mode === 'express'){
                await this.$refs.paymentExpress.handleServerResponse(response.data);
              } else {
                await this.$refs.payment.handleServerResponse(response.data);
              }
            } else {
              this.setBooking(response.data?.booking);
              this.setBookingToken(
                response.data?.retailBooking?.token ??
                response.data?.booking?.token ??
                response.data?.token ??
                false
              );
              this.setStatus("success");
              trackBookingComplete(response.data?.booking)
              this.$emit('setPage', 'thanks');
              this.$emit('top')
            }

          })
          .catch((error) => {
            console.error(error);
            console.log("THE ERROR", error.response);

            if (error?.response?.data?.requestStatus?.error) {
              this.submitErrors.push(error?.response.data?.requestStatus?.error?.errorMessage);
            }

            if (error?.response?.data?.errors?.errorMessage) {
              this.submitErrors.push(error.response.data.errors.errorMessage);
            }

            if (error?.response?.data?.errors && error.response.data?.errors?.errorCode !== "1") {
              for (const key in error.response.data.errors) {
                if(key !== "errorCode"){
                  if (Array.isArray(error.response.data.errors[key])){
                    this.submitErrors = [...this.submitErrors, ...error.response.data.errors[key]];
                  } else {
                    this.submitErrors = [...this.submitErrors, error.response.data.errors[key]];
                  }
                }
              }
            }

            this.setStatus("failed");
          })
          .then(() => {
            this.loading = false;
            this.bookingProcessing = false;
          });
    },

    addQuoteErrors(errors) {
      this.submitQuoteErrors = errors;
    },
    addQuestionErrors(errors) {
      this.questionErrors = errors;
    },
    addPaymentErrors(errors) {
      this.paymentErrors = errors;

      if (errors.length <= 0) {
        return;
      }

      const errorInput = document.querySelector(`#paymentForm`);

      if (errorInput) {
        errorInput.focus();
        errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onInvalidSubmit({ values, errors, results }) {
      this.focusFirstError(errors);
    }
  },
};
</script>
